/**
 * EmailOutboxDialog — review every email an approval will send before sending it.
 *
 * Lists each recipient with the subject and what is personal about their email, renders any
 * recipient's email exactly as it will be sent, lists who is skipped and why, and refuses to send
 * if an address would get more than one email.
 *
 * Props
 *   loadOutbox()        → Promise<{ emails: [{key, name, email, subject, detail}], skipped: [{key, name, email, reason}] }>
 *   loadPreview(email)  → Promise<string>  (rendered HTML)
 *   onSend(count)       → Promise          (performs the send; throw to keep the dialog open)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, InputAdornment, List, ListItemButton, ListItemText, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon, Download as DownloadIcon, MarkEmailRead as EmailIcon, NavigateBefore as PrevIcon,
  NavigateNext as NextIcon, Search as SearchIcon, Send as SendIcon,
} from '@mui/icons-material';

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const downloadCsv = (emails, skipped, filename) => {
  const rows = [['status', 'name', 'email', 'subject', 'detail / reason']]
    .concat(emails.map((e) => ['send', e.name, e.email, e.subject, e.detail]))
    .concat(skipped.map((s) => ['skipped', s.name, s.email, '', s.reason]));
  const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function EmailOutboxDialog({
  open, onClose, title = 'Review emails', description, loadOutbox, loadPreview, onSend,
  sendLabel = 'Send', csvName = 'outbox.csv',
}) {
  const [outbox, setOutbox] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('send');
  const [query, setQuery] = useState('');
  const [current, setCurrent] = useState(null);       // key of the email shown
  const [html, setHtml] = useState('');
  const [previewState, setPreviewState] = useState('idle'); // idle | loading | error
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const cache = useRef(new Map());

  // Load the outbox each time the dialog opens
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    cache.current = new Map();
    setOutbox(null); setError(null); setTab('send'); setQuery(''); setCurrent(null);
    setHtml(''); setConfirming(false); setSendError(null);
    loadOutbox()
      .then((data) => {
        if (cancelled) return;
        setOutbox({ emails: data.emails || [], skipped: data.skipped || [] });
        setCurrent(data.emails?.[0]?.key ?? null);
      })
      .catch((e) => !cancelled && setError(e.response?.data?.error || e.message || 'Could not load the emails.'));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const emails = useMemo(() => outbox?.emails || [], [outbox]);
  const skipped = useMemo(() => outbox?.skipped || [], [outbox]);

  // An address listed twice would get two emails: never allowed
  const duplicates = useMemo(() => {
    const seen = new Map();
    emails.forEach((e) => {
      const k = (e.email || '').trim().toLowerCase();
      seen.set(k, (seen.get(k) || 0) + 1);
    });
    return [...seen.entries()].filter(([, n]) => n > 1).map(([address]) => address);
  }, [emails]);

  const q = query.trim().toLowerCase();
  const match = (r) => !q || [r.name, r.email, r.subject, r.detail, r.reason].some((v) => (v || '').toLowerCase().includes(q));
  const shownEmails = useMemo(() => emails.filter(match), [emails, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownSkipped = useMemo(() => skipped.filter(match), [skipped, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentEmail = emails.find((e) => e.key === current) || null;
  const index = currentEmail ? emails.indexOf(currentEmail) : -1;

  const showPreview = useCallback(async (email) => {
    if (!email) return;
    if (cache.current.has(email.key)) { setHtml(cache.current.get(email.key)); setPreviewState('idle'); return; }
    setPreviewState('loading');
    try {
      const body = await loadPreview(email);
      cache.current.set(email.key, body);
      setHtml(body);
      setPreviewState('idle');
    } catch {
      setPreviewState('error');
    }
  }, [loadPreview]);

  useEffect(() => { if (currentEmail) showPreview(currentEmail); }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = (delta) => {
    const next = emails[index + delta];
    if (next) setCurrent(next.key);
  };

  const send = async () => {
    setSending(true);
    setSendError(null);
    try {
      await onSend(emails.length);
    } catch (e) {
      setSendError(e.response?.data?.error || e.message || 'Sending failed.');
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  const uniqueRecipients = new Set(emails.map((e) => (e.email || '').toLowerCase())).size;

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="xl" fullWidth
            PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <EmailIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
          {description && <Typography variant="body2" color="text.secondary">{description}</Typography>}
        </Box>
        <IconButton onClick={onClose} disabled={sending} aria-label="Close"><CloseIcon /></IconButton>
      </DialogTitle>

      {outbox && (
        <Stack direction="row" spacing={1} sx={{ px: 3, pb: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip color="primary" label={`${emails.length} email${emails.length === 1 ? '' : 's'} will be sent`} />
          <Chip variant="outlined" label={`${uniqueRecipients} recipient${uniqueRecipients === 1 ? '' : 's'}`} />
          <Chip variant="outlined" color={skipped.length ? 'warning' : 'default'}
                label={`${skipped.length} skipped`} onClick={skipped.length ? () => setTab('skipped') : undefined} />
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadCsv(emails, skipped, csvName)}>
            Recipient list (.csv)
          </Button>
        </Stack>
      )}
      {duplicates.length > 0 && (
        <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
          {duplicates.length} address(es) would get more than one email: {duplicates.slice(0, 5).join(', ')}
          {duplicates.length > 5 ? '…' : ''}. Sending is blocked; each person must get one email.
        </Alert>
      )}
      <Divider />

      <DialogContent sx={{ p: 0, display: 'flex', minHeight: 0 }}>
        {!outbox && !error && (
          <Box sx={{ m: 'auto', textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={1}>Building every email…</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ m: 3, flex: 1, alignSelf: 'flex-start' }}>{error}</Alert>}

        {outbox && (
          <>
            {/* Recipients */}
            <Box sx={{ width: 380, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box sx={{ p: 1.5, pb: 0 }}>
                <TextField size="small" fullWidth placeholder="Search name, email, content…" value={query}
                           onChange={(e) => setQuery(e.target.value)}
                           InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ minHeight: 40 }}>
                  <Tab value="send" label={`Will receive (${shownEmails.length})`} sx={{ minHeight: 40, textTransform: 'none' }} />
                  <Tab value="skipped" label={`Skipped (${shownSkipped.length})`} sx={{ minHeight: 40, textTransform: 'none' }} />
                </Tabs>
              </Box>
              <List dense sx={{ overflowY: 'auto', flex: 1 }}>
                {tab === 'send' && shownEmails.map((e) => (
                  <ListItemButton key={e.key} selected={e.key === current} onClick={() => setCurrent(e.key)}
                                  sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={<><strong>{e.name || e.email}</strong> <Typography component="span" variant="caption" color="text.secondary">{e.email}</Typography></>}
                      secondary={e.detail || e.subject}
                      secondaryTypographyProps={{ sx: { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } }}
                    />
                  </ListItemButton>
                ))}
                {tab === 'skipped' && shownSkipped.map((s) => (
                  <Box key={s.key || s.email} sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2"><strong>{s.name || s.email}</strong> <Typography component="span" variant="caption" color="text.secondary">{s.email}</Typography></Typography>
                    <Typography variant="caption" color="warning.dark">{s.reason}</Typography>
                  </Box>
                ))}
                {((tab === 'send' && !shownEmails.length) || (tab === 'skipped' && !shownSkipped.length)) && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {q ? 'No match.' : tab === 'send' ? 'Nobody will get an email.' : 'Nobody is skipped.'}
                  </Typography>
                )}
              </List>
            </Box>

            {/* Email */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {currentEmail ? (
                <>
                  <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap><strong>To:</strong> {currentEmail.name} &lt;{currentEmail.email}&gt;</Typography>
                      <Typography variant="body2" noWrap><strong>Subject:</strong> {currentEmail.subject}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{index + 1} of {emails.length}</Typography>
                    <Tooltip title="Previous"><span><IconButton size="small" aria-label="Previous email" disabled={index <= 0} onClick={() => step(-1)}><PrevIcon /></IconButton></span></Tooltip>
                    <Tooltip title="Next"><span><IconButton size="small" aria-label="Next email" disabled={index >= emails.length - 1} onClick={() => step(1)}><NextIcon /></IconButton></span></Tooltip>
                  </Box>
                  <Box sx={{ flex: 1, position: 'relative', bgcolor: 'grey.100' }}>
                    {previewState === 'loading' && <CircularProgress size={28} sx={{ position: 'absolute', top: 16, right: 16 }} />}
                    {previewState === 'error'
                      ? <Alert severity="error" sx={{ m: 2 }}>Could not render this email.</Alert>
                      : <iframe title="Email preview" srcDoc={html} sandbox="" style={{ width: '100%', height: '100%', border: 0, background: '#fff' }} />}
                  </Box>
                </>
              ) : (
                <Typography color="text.secondary" sx={{ m: 'auto' }}>Nothing to preview.</Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {sendError && <Alert severity="error" sx={{ mr: 'auto', py: 0 }}>{sendError}</Alert>}
        {confirming && !sendError && (
          <Typography variant="body2" color="warning.dark" sx={{ mr: 'auto' }}>
            This sends {emails.length} email{emails.length === 1 ? '' : 's'} now and cannot be undone.
          </Typography>
        )}
        <Button onClick={confirming ? () => setConfirming(false) : onClose} disabled={sending}>
          {confirming ? 'Back' : 'Cancel'}
        </Button>
        <Button
          variant="contained" color={confirming ? 'error' : 'primary'}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={!outbox || !emails.length || duplicates.length > 0 || sending}
          onClick={confirming ? send : () => setConfirming(true)}
        >
          {confirming ? `Confirm: send ${emails.length}` : `${sendLabel} ${emails.length} email${emails.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
